import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import { Badge, Card, ErrorBanner, PageHeader, Spinner } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";

const pct = (v: number | null) => (v === null ? "—" : `${v.toFixed(1)}%`);
const num = (v: number | null) => (v === null ? "—" : v.toLocaleString());

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export default function HealthPage() {
  const { data, error, isPending } = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 60_000,
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorBanner message={errorMessage(error)} />;

  return (
    <>
      <PageHeader
        title="Watcher Health"
        actions={<Badge tone={data.status}>{data.status}</Badge>}
      />
      {data.alerts.length > 0 && (
        <ul className="mb-6 space-y-2">
          {data.alerts.map((a, i) => (
            <li
              key={i}
              className={`rounded p-3 text-sm ${a.severity === "critical" ? "bg-red-50 text-red-800" : "bg-yellow-50 text-yellow-800"}`}
            >
              <strong className="uppercase">{a.severity}</strong> {a.message}
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card title="Webhook">
          <Stat
            label="Last event"
            value={formatDateTime(data.webhook.lastEventAt)}
          />
          <Stat label="Events (7d)" value={num(data.webhook.eventsLast7d)} />
          <Stat label="Coverage" value={pct(data.webhook.coveragePct)} />
        </Card>
        <Card title="Crawl">
          <Stat
            label="Last crawl"
            value={formatDateTime(data.crawl.lastCrawlAt)}
          />
          <Stat label="Jobs (7d)" value={num(data.crawl.jobsLast7d)} />
          <Stat label="Failed (7d)" value={num(data.crawl.failedLast7d)} />
        </Card>
        <Card title="Queue">
          <Stat label="Depth" value={num(data.queue.depth)} />
          <Stat label="Dead-letter depth" value={num(data.queue.dlqDepth)} />
        </Card>
        <Card title="Files">
          <Stat label="Total" value={num(data.files.total)} />
          <Stat label="Active" value={num(data.files.active)} />
          <Stat label="Deleted" value={num(data.files.deleted)} />
        </Card>
        <Card title="Accuracy">
          <Stat label="Accuracy" value={pct(data.accuracyPct)} />
        </Card>
      </div>
    </>
  );
}
